import React, { useState, useEffect } from "react";
import { connect, useDispatch } from "react-redux";

import {
  Input,
  Row,
  Typography,
  Col,
  Card,
  Select,
  Button,
  Form,
  Divider,
  Spin,
  DatePicker,
} from "antd";
import { PlusOutlined, MinusOutlined } from "@ant-design/icons";
import { useNavigate, Link } from "react-router-dom";
import CustomButton from "../../../components/custom/CustomButton";

//PROJECT IMPORTS
import appStatic from "../../../config/appStatic";
import appLabel from "../../../config/appLabel";
import cnt from "../../../services/definedConstant";
import { BOM } from "../../../services/definedMethod";
import definedApi from "../../../services/definedApi";
import TopContent from "../../../components/layouts/TopContent";
import MainLayout from "../../../components/layouts/MainLayout";
import AffectedGLEntries from "./component/AffectedGLEntries";
import { showCheckerApprovalModal } from "../../../components/CheckerModal";

const GLA0600 = (props) => {
  //DECLARED VARIABLES
  const { userData } = props;
  const { session } = userData;
  const { Text, Title } = Typography;
  const { TextArea } = Input;

  const navigate = useNavigate();
  const [accountData, setAccountData] = useState([]);
  const [officeData, setOfficeData] = useState([]);
  const [currencyData, setCurrencyData] = useState([]);
  const [paymentTypeData, setpaymentTypeData] = useState([]);
  const [debitAccountData, setDebitAccountData] = useState([]);
  const [creditAccountData, setCreditAccountData] = useState([]);
  const [frequentPostingArr, setfrequentPostingArr] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDebitCredit, setShowDebitCredit] = useState(false);
  const [onShowPaymentDetails, setOnShowPaymentDetails] = useState(false);

  useEffect(() => {
    getOffice();
    getCurrency();
    getPaymentType();
    getAccountingRules();
  }, []);

  function onCancel() {
    navigate("/dashboard", { replace: false });
  }

  function onSelectAccountingRule(idParam) {
    setShowDebitCredit(true);

    let tempDebit = [];
    let tempCredit = [];

    const tempAccountingRule = frequentPostingArr?.find(
      (element) => element.id === idParam
    );

    tempAccountingRule?.debitAccounts?.forEach((element) => {
      tempDebit.push({
        value: element.id,
        label: `${element.glCode} (${element.name})`,
      });
    });

    tempAccountingRule?.creditAccounts?.forEach((element) => {
      tempCredit.push({
        value: element.id,
        label: `${element.glCode} (${element.name})`,
      });
    });

    setDebitAccountData(tempDebit);
    setCreditAccountData(tempCredit);
    setIsLoading(false);
  }

  function getOffice() {
    let body = {
      session,
    };

    BOM.FetchReqAction(body, definedApi.GetOfficesList, (err, res) => {
      if (err) {
        BOM.AlertMsg(cnt.DANGER, err.errors[0].defaultUserMessage);
      } else {
        const objList = res;
        let temp = [];

        objList.forEach((element) => {
          temp.push({
            value: element.id,
            label: element.name,
          });
        });

        setOfficeData(temp);
        setIsLoading(false);
      }
    });
  }

  function getCurrency() {
    let body = {
      session,
    };

    BOM.FetchReqAction(body, definedApi.GetCurrenciesList, (err, res) => {
      if (err) {
        BOM.AlertMsg(cnt.DANGER, err.errors[0].defaultUserMessage);

        setIsLoading(false);
      } else {
        const objList = res.selectedCurrencyOptions;
        let temp = [];

        objList.forEach((element) => {
          temp.push({
            value: element.code,
            label: element.displayLabel,
          });
        });

        setCurrencyData(temp);
        setIsLoading(false);
      }
    });
  }

  function getAccountingRules() {
    let body = {
      session,
    };

    BOM.FetchReqAction(body, definedApi.AccountingRulesList, (err, res) => {
      if (err) {
        BOM.AlertMsg(cnt.DANGER, err.errors[0].defaultUserMessage);
        setIsLoading(false);
      } else {
        const objList = res;
        let temp = [];

        setfrequentPostingArr(objList);

        objList.forEach((element) => {
          temp.push({
            value: element.id,
            label: element.name,
          });
        });

        setAccountData(temp);
        setIsLoading(false);
      }
    });
  }

  function getPaymentType() {
    let body = {
      session,
    };

    BOM.FetchReqAction(body, definedApi.GetPaymentTypesList, (err, res) => {
      if (err) {
        BOM.AlertMsg(cnt.DANGER, err.errors[0].defaultUserMessage);
        setIsLoading(false);
      } else {
        const objList = res;
        let temp = [];

        objList.forEach((element) => {
          temp.push({
            value: element.id,
            label: element.name,
          });
        });

        setpaymentTypeData(temp);
        setIsLoading(false);
      }
    });
  }

  // util to validate debit-credit pairs
  const validateOneToOneMatch = (debits, credits) => {
    const debitAmounts = debits
      .map((d) => Number(d.amount))
      .filter((a) => !isNaN(a));
    const creditAmounts = credits
      .map((c) => Number(c.amount))
      .filter((a) => !isNaN(a));

    const totalDebit = debitAmounts.reduce((sum, a) => sum + a, 0);
    const totalCredit = creditAmounts.reduce((sum, a) => sum + a, 0);

    if (totalDebit !== totalCredit) {
      return {
        valid: false,
        message: `Total mismatch: Debits = ${totalDebit}, Credits = ${totalCredit}`,
      };
    }

    // Case 1: Equal counts → must be a permutation (order doesn't matter)
    if (debitAmounts.length === creditAmounts.length) {
      const sortedDebits = [...debitAmounts].sort((a, b) => a - b);
      const sortedCredits = [...creditAmounts].sort((a, b) => a - b);

      for (let i = 0; i < sortedDebits.length; i++) {
        if (sortedDebits[i] !== sortedCredits[i]) {
          return {
            valid: false,
            message:
              "Debits and credits must match as sets when counts are equal.",
          };
        }
      }
      return { valid: true };
    }

    // Case 2: Counts differ → only valid if it's strictly 1-to-many
    if (
      (debitAmounts.length === 1 && creditAmounts.length > 1) ||
      (creditAmounts.length === 1 && debitAmounts.length > 1)
    ) {
      return { valid: true }; // allowed as long as totals already match
    }

    // Case 3: Any other many-to-many mismatch is invalid
    return {
      valid: false,
      message:
        "Invalid entry counts: only equal counts or 1-to-many relationships are allowed.",
    };
  };

  const onSubmit = (formValues) => {
    console.log(formValues);
    const debitEntries = formValues.debits || [];
    const creditEntries = formValues.credits || [];

    const result = validateOneToOneMatch(debitEntries, creditEntries);
    console.log(result);

    if (!result.valid) {
      BOM.AlertMsg(cnt.DANGER, result.message);
      return;
    }

    formValues.transactionDate = BOM.DateTime(
      formValues.transactionDate,
      "only-date"
    );

    const body = {
      ...formValues,
      locale: "en",
      dateFormat: "dd MMMM yyyy",
      method: "POST",
      session,
    };

    setIsLoading(true);
    BOM.FetchReqAction(
      body,
      definedApi.FrequentPosting,
      (err, res) => {
        if (err) {
          // BOM.AlertMsg(cnt.DANGER, err.defaultUserMessage);
          BOM.AlertMsg(cnt.DANGER, err?.errors[0]?.defaultUserMessage);
          setIsLoading(false);
        } else {
          BOM.AlertMsg(cnt.SUCCESS, appLabel.newFrequentPostingMsg);
          navigate("/view-gla0600", { state: res });
          //STOP LOAD
          setIsLoading(false);
        }
      },
      {
        navigate,
        showCheckerModal: showCheckerApprovalModal,
        onCheckerApproval: (res) => {
          navigate("/view-gla0600", {
            state: {
              transactionId: res.transactionId,
            },
          });
          setIsLoading(false);
        },
      }
    );
  };

  return (
    <MainLayout isEnableRightSidebar={false} onSave={onSubmit}>
      <TopContent
        pageTitle={appLabel.FrequentPosting}
        pageItems={appStatic.frequentPostingItems}
      />
      <Spin spinning={isLoading} size="large">
        <Card
          title={
            <Title level={4} style={{ color: "#000" }}>
              {appLabel.generalInfo}
            </Title>
          }
          headStyle={{ borderBottomWidth: 0 }}
          style={{
            boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
            borderRadius: 20,
          }}
        >
          <Form layout="vertical" onFinish={onSubmit}>
            <Card
              style={{
                marginBottom: "3%",
                boxShadow: "0px 0px 30px 5px rgba(0,0,0,0.025)",
              }}
              bordered
            >
              <Row style={{ justifyContent: "space-between" }}>
                <Form.Item
                  name="officeId"
                  label={<Text strong>Office</Text>}
                  rules={[
                    {
                      required: true,
                      message: "Required Field",
                    },
                  ]}
                >
                  <Select
                    placeholder={"Please Select a value"}
                    style={{ width: "30vh" }}
                    showSearch
                    options={officeData}
                  />
                </Form.Item>
                <Form.Item
                  name="accountingRule"
                  label={<Text strong>Accounting Rule</Text>}
                  rules={[
                    {
                      required: true,
                      message: "Required Field",
                    },
                  ]}
                >
                  <Select
                    placeholder={"Please Select a value"}
                    style={{ width: "30vh" }}
                    showSearch
                    options={accountData}
                    onChange={onSelectAccountingRule}
                  />
                </Form.Item>
                <Form.Item
                  name="currencyCode"
                  label={<Text strong>Currency</Text>}
                  rules={[
                    {
                      required: true,
                      message: "Required Field",
                    },
                  ]}
                >
                  <Select
                    placeholder={"Please Select a value"}
                    style={{ width: "30vh" }}
                    showSearch
                    options={currencyData}
                  />
                </Form.Item>
              </Row>

              {showDebitCredit && (
                <>
                  <Divider orientation="left" orientationMargin="0">
                    {"Affected GL Entries"}
                  </Divider>

                  <div>
                    <AffectedGLEntries
                      accountData={debitAccountData}
                      labelName={"Debit"}
                      accountName={"debits"}
                    />
                  </div>

                  <div>
                    <AffectedGLEntries
                      accountData={creditAccountData}
                      labelName={"Credit"}
                      accountName={"credits"}
                    />
                  </div>
                </>
              )}

              <Row style={{ justifyContent: "space-between" }}>
                <Form.Item label={<Text strong>Show Payment Details</Text>}>
                  <div style={{ width: "30vh" }}>
                    <Button
                      onClick={() =>
                        setOnShowPaymentDetails(!onShowPaymentDetails)
                      }
                      type="primary"
                      icon={
                        !onShowPaymentDetails ? (
                          <PlusOutlined />
                        ) : (
                          <MinusOutlined />
                        )
                      }
                      style={{
                        backgroundColor: "#92428B",
                        color: "white",
                        width: "15vh",
                        marginBottom: 20,
                      }}
                    >
                      Show
                    </Button>
                  </div>
                </Form.Item>
                <Form.Item
                  name="referenceNumber"
                  label={<Text strong>Reference Number</Text>}
                >
                  <Input placeholder="Enter Number" style={{ width: "30vh" }} />
                </Form.Item>
                <Form.Item
                  name="transactionDate"
                  label={<Text strong>Transaction Date</Text>}
                  rules={[
                    {
                      required: true,
                      message: "Required Field",
                    },
                  ]}
                >
                  <DatePicker style={{ width: "30vh" }} />
                </Form.Item>
              </Row>

              {onShowPaymentDetails && (
                <>
                  <Row style={{ justifyContent: "space-between" }}>
                    <Form.Item
                      name="paymentTypeId"
                      label={<Text strong>Payment Types</Text>}
                    >
                      <Select
                        style={{ width: "30vh" }}
                        options={paymentTypeData}
                        placeholder="Please Select a value"
                      />
                    </Form.Item>
                    <Form.Item
                      name="accountNumber"
                      label={<Text strong>Account Number</Text>}
                    >
                      <Input
                        placeholder="Enter Number"
                        style={{ width: "30vh" }}
                      />
                    </Form.Item>
                    <Form.Item
                      name="checkNumber"
                      label={<Text strong>Cheque</Text>}
                    >
                      <Input
                        style={{ width: "30vh" }}
                        placeholder="Enter Detail"
                      />
                    </Form.Item>
                  </Row>
                  <Row style={{ justifyContent: "space-between" }}>
                    <Form.Item
                      name="routingCode"
                      label={<Text strong>Routing Code</Text>}
                    >
                      <Input
                        placeholder="Enter Number"
                        style={{ width: "30vh" }}
                      />
                    </Form.Item>
                    <Form.Item
                      name="receiptNumber"
                      label={<Text strong>Receipt Number</Text>}
                    >
                      <Input
                        placeholder="Enter Number"
                        style={{ width: "30vh" }}
                      />
                    </Form.Item>
                    <Form.Item
                      name="bankNumber"
                      label={<Text strong>Bank Number</Text>}
                    >
                      <Input
                        style={{ width: "30vh" }}
                        placeholder="Enter Number"
                      />
                    </Form.Item>
                  </Row>
                </>
              )}

              <Row>
                <Form.Item
                  name="comments"
                  label={<Text strong>Comments</Text>}
                  style={{ width: "53%" }}
                >
                  <TextArea
                    showCount
                    maxLength={100}
                    // onChange={onChange}
                    placeholder="Enter Account description"
                    style={{
                      height: "15vh",
                      width: "45vh",
                    }}
                  ></TextArea>
                </Form.Item>
              </Row>
            </Card>
            <Form.Item>
              <Row>
                {BOM.HasPermission("CREATE_JOURNALENTRY") && (
                  <CustomButton
                    content="Submit"
                    style={{
                      width: "10%",
                      marginLeft: "auto",
                      boxShadow: "0px 0px 10px 5px rgba(0,0,0,0.1)",
                    }}
                    htmlType="submit"
                    backgroundColor="#92428B"
                    colorText="white"
                    hoverBackground="#AD4EA7"
                  />
                )}
                <CustomButton
                  content="Cancel"
                  style={{
                    width: "10%",
                    marginLeft: "1%",
                    boxShadow: "0px 0px 10px 5px rgba(0,0,0,0.1)",
                  }}
                  backgroundColor="#fff"
                  colorText="black"
                  hoverBackground="#F5F5F5"
                  onClick={onCancel}
                />
              </Row>
            </Form.Item>
          </Form>
        </Card>
      </Spin>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  userData: state.AuthenticationData.userData,
  rightBtn: state.RightSideBtn,
});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(GLA0600);
